'use client'

import { useMemo } from 'react'

import cn from 'clsx'
import { Button } from '@/components/Button/Button'

import { Text } from '@/components/Text/Text'

import css from './UploadIndicator.module.css'

import { Box, type BoxProps } from '@/components/Box/Box'
import { UploadImage, UploadDoc, UploadPdf, UploadVideo, MagnifyingGlass } from '@doist/icons/base'
import { X } from '@doist/icons/brand'

type UploadIndicatorState =
    | { type: 'uploading'; progress: number }
    | { type: 'failed'; progress: number }
    | { type: 'completed' }

type UploadIndicatorProps = {
    filename: string
    uploadingLabel: string
    uploadCompletedLabel: string
    uploadFailedLabel: string
    removeFileLabel: string
    openPreviewLabel: string
    fileType?: 'doc' | 'image' | 'pdf' | 'video'
    state?: UploadIndicatorState
    onDelete?: () => void
    onOpen?: () => void
    disabled?: boolean
} & BoxProps

export function UploadIndicator({
    fileType = 'image',
    filename,
    state = { type: 'uploading', progress: 0 },
    onDelete,
    className,
    onOpen,
    disabled = false,
    uploadingLabel = 'Uploading…',
    uploadCompletedLabel = 'Upload completed!',
    uploadFailedLabel = 'Upload failed!',
    removeFileLabel = 'Remove file',
    openPreviewLabel = 'Open preview',
    ...rest
}: UploadIndicatorProps) {
    const title = useMemo(() => {
        if (state.type === 'uploading') {
            if (state.progress >= 100) {
                return uploadCompletedLabel
            }
            return uploadingLabel
        }
        if (state.type === 'completed') {
            return filename
        }
        if (state.type === 'failed') {
            return uploadFailedLabel
        }
        return filename
    }, [filename, state, uploadCompletedLabel, uploadFailedLabel, uploadingLabel])

    const Icon = useMemo(() => {
        if (fileType === 'video') {
            return UploadVideo
        }
        if (fileType === 'doc') {
            return UploadDoc
        }
        if (fileType === 'pdf') {
            return UploadPdf
        }
        return UploadImage
    }, [fileType])

    const textSize = 'body-2xs'

    return (
        <Box className={cn(css.uploadIndicator, className)} {...rest}>
            <Box className={css.fileIcon}>
                <Icon />
            </Box>

            <Box className={css.mainContent}>
                <Box className={css.details}>
                    <Text className={css.title} size={textSize}>
                        {title}
                    </Text>
                    {(state.type === 'uploading' || state.type === 'failed') && (
                        <Text className={css.progress} size={textSize}>
                            {state.progress.toFixed(0)}%
                        </Text>
                    )}
                </Box>

                {(state.type === 'uploading' || state.type === 'failed') && (
                    <Box className={css.progressBarContainer}>
                        <Box
                            className={cn(css.progressBar, {
                                [css.error]: state.type === 'failed',
                            })}
                            style={{ width: `${state.progress.toFixed(0)}%` }}
                        />
                    </Box>
                )}
            </Box>

            <Box className={css.actionBtns}>
                {state.type === 'completed' && onOpen ? (
                    <Button
                        data-testid="preview-btn"
                        aria-label={openPreviewLabel}
                        variant="quaternary"
                        size="small"
                        onClick={onOpen}
                        startIcon={MagnifyingGlass}
                        disabled={disabled}
                    />
                ) : null}

                {onDelete ? (
                    <Button
                        data-testid="close-btn"
                        aria-label={removeFileLabel}
                        variant="quaternary"
                        size="small"
                        onClick={onDelete}
                        startIcon={X}
                        disabled={disabled}
                    />
                ) : null}
            </Box>
        </Box>
    )
}

export type { UploadIndicatorProps, UploadIndicatorState }
